import * as Commons from "../oc-commons/Commons";

let pixel_per_mm_x: number | null = null;
let pixel_per_mm_y: number | null = null;
const weakDataMap = new WeakMap<HTMLElement, Record<string, unknown>>();

/**
 * Statische Utility-Klasse zum DOM.
 */

/**
 * erzeugt ein HTMLElement aus einem html string
 * @param {string} htmlString
 * @returns {HTMLElement}
 */
export function createElementFromHTML(htmlString: string): HTMLElement {
  const div = document.createElement("div");
  div.innerHTML = htmlString.trim();
  return div.firstChild as HTMLElement;
}

/**
 * Ruft callback auf, sobald alle events des eventNames-Arrays auf dem element ausgelöst worden sind.
 * @param {HTMLElement} element
 * @param {string[]} eventNames
 * @param {function} callback
 */
export function waitUntilAllEventsHappened(
  element: HTMLElement,
  eventNames: string[],
  callback: () => void
): void {
  const events = new Set(eventNames);
  eventNames.forEach((event) => {
    const listener = (): void => {
      events.delete(event);
      if (events.size === 0) {
        callback();
      }
    };
    element.addEventListener(event, listener, { once: true });
  });
}

/**
 * gibt das Bild als Base64 - encodetes JPG zurück,
 * wobei die data:image - Einleitung entfernt wird.
 * @param {Canvas|Image} canvasOrImage
 * @returns {string}
 * */
export function getAsJpgBase64Encoded(
  canvasOrImage: HTMLCanvasElement | HTMLImageElement
): string {
  const imageData = (canvasOrImage as HTMLCanvasElement).toDataURL("image/jpeg");
  return imageData.replace(/^data:image\/(png|jpeg);base64,/, "");
}

/**
 * Gibt die Pixel pro MM zurück
 * @param {string} dimension: 'x' oder 'y'
 * @returns {number}
 */
export function getPixelPerMM(dimension: "x" | "y" = "x"): number {
  return dimension == "x" ? getPixelPerMMForX() : getPixelPerMMForY();
}

/**
 * Gibt die Pixel pro MM zurück in der X-Achse
 * @returns {number}
 */
export function getPixelPerMMForX(): number {
  if (pixel_per_mm_x == null) {
    const div = document.createElement("div");
    div.style.width = "1mm";
    const body = document.getElementsByTagName("body")[0];
    body.appendChild(div);
    const ppm = document.defaultView!.getComputedStyle(div, null).getPropertyValue("width");
    body.removeChild(div);
    pixel_per_mm_x = parseFloat(ppm);
  }
  return pixel_per_mm_x;
}

/**
 * Gibt die Pixel pro MM zurück in der Y-Achse
 * @returns {number}
 */
export function getPixelPerMMForY(): number {
  if (pixel_per_mm_y == null) {
    const div = document.createElement("div");
    div.style.height = "1mm";
    const body = document.getElementsByTagName("body")[0];
    body.appendChild(div);
    const ppm = document.defaultView!.getComputedStyle(div, null).getPropertyValue("height");
    body.removeChild(div);
    pixel_per_mm_y = parseFloat(ppm);
  }
  return pixel_per_mm_y;
}

type CssUnit =
  | "px"
  | "%"
  | "em"
  | "mm"
  | "cm"
  | "in"
  | "pt"
  | "pc"
  | "rem"
  | "vw"
  | "vh"
  | "vmin"
  | "vmax";

/**
 * Konvertiert einen CSS-Wert wie 5em in die Einheit px um.
 * @param {number} value der Zahlenwert
 * @param {?string} unit die Einheit des Zahlenwertes wie em, %, mm usw.
 * @param {HTMLElement} containerElement
 * @param {string} [dimension='x'] entweder 'x' oder 'y'
 * @returns {number} containerWidth
 * @returns {number} containerHeight
 */
export function convertUnitToPX(
  value: number,
  unit: CssUnit | null,
  containerElement: HTMLElement,
  dimension: "x" | "y" = "x",
  containerWidth?: number,
  containerHeight?: number
): number {
  switch (unit) {
    case "px":
      return value;
    case "%":
      return (
        ((dimension == "x"
          ? containerWidth == null
            ? containerElement.clientWidth
            : containerWidth
          : containerHeight == null
            ? containerElement.clientHeight
            : containerHeight) /
          100) *
        value
      );
    case "em":
      return parseFloat(getComputedStyle(containerElement).fontSize) * value;
    case "mm":
      return getPixelPerMM(dimension) * value;
    case "cm":
      return getPixelPerMM(dimension) * 10 * value;
    case "in":
      return getPixelPerMM(dimension) * 25.4 * value;
    case "pt":
      return ((getPixelPerMM(dimension) * 1) / 72) * 25.4 * value;
    case "pc":
      return ((getPixelPerMM(dimension) * 1) / 6) * 25.4 * value;
    case "rem":
      return parseFloat(getComputedStyle(document.body).fontSize) * value;
    case "vw":
      return (document.documentElement.clientWidth / 100) * value;
    case "vh":
      return (document.documentElement.clientHeight / 100) * value;
    case "vmin":
      return (
        (Math.min(document.documentElement.clientWidth, document.documentElement.clientHeight) /
          100) *
        value
      );
    case "vmax":
      return (
        (Math.max(document.documentElement.clientWidth, document.documentElement.clientHeight) /
          100) *
        value
      );
  }
  return value;
}

/**
 * ruft callback auf, sobald element zum Dom hinzugefügt ist.
 * @param {HTMLElement} element
 * @param {function} callback
 * */
export function onAttachToDom(element: HTMLElement, callback: () => void): void {
  Commons.delayUntil(callback, () => element.isConnected, 60000, 100);
}

/**
 * ruft callback auf, sobald element vom Dom entfernt ist.
 * @param {HTMLElement} element
 * @param {function} callback
 */
export function onDetachFromDom(element: HTMLElement, callback: () => void): void {
  Commons.delayUntil(callback, () => !element.isConnected, 60000, 100);
}

// Erweitere HTMLElement Interface für private Properties
interface HTMLElementWithExtensions extends HTMLElement {
  _resizeInterval?: ReturnType<typeof setInterval>;
  ___measureTextContext?: CanvasRenderingContext2D;
  __onRefInitialized?: boolean;
}

/**
 * ruft callback auf, wenn sich die Größe von element ändert, sowie initial einmal,
 * sobald element zum Dom hinzugefügt ist
 * @param {HTMLElement} element
 * @param {function} callback
 */
export function onResize(element: HTMLElement, callback: () => void): void {
  onAttachToDom(element, () => {
    const el = element as HTMLElementWithExtensions;
    if (el._resizeInterval != null) {
      throw new Error("only one onResize Listener is allowed per Element");
    }
    callback();
    let oldWidth = element.clientWidth;
    let oldHeight = element.clientHeight;
    el._resizeInterval = setInterval(() => {
      if (!element.isConnected) {
        clearInterval(el._resizeInterval!);
        delete el._resizeInterval;
        return;
      }
      const width = element.clientWidth;
      const height = element.clientHeight;
      if (oldWidth != width || oldHeight != height) {
        oldWidth = width;
        oldHeight = height;
        callback();
      }
    }, 1000);
  });
}

/**
 * unbinded das resize event
 * @param {HTMLElement} element
 */
export function offResize(element: HTMLElement): void {
  const el = element as HTMLElementWithExtensions;
  if (el._resizeInterval != null) {
    clearInterval(el._resizeInterval);
    delete el._resizeInterval;
  }
}

/**
 * gibt die ID oder tag + Klasse in Form eines CSS-Selektors zurück
 * @param {HTMLElement} element
 * @returns {string}
 */
export function debugName(element: HTMLElement): string {
  const id = element.getAttribute("id");
  if (id) {
    return "#" + id;
  }
  const tagName = element.tagName.toLowerCase();
  const classNames = (element.getAttribute("class") || "")
    .split(" ")
    .filter((x) => !Commons.isEmpty(x))
    .join(".");
  return tagName + (classNames.length > 0 ? "." + classNames : "");
}

/**
 * Berechnet die width in pixel zu einem Element als Kontext (für die Font-Ermittlung) und einem Text.
 * @param element
 * @param text
 * @returns {number}
 */
export function measureTextWidth(element: HTMLElement, text: string): number {
  return measureTextMetrics(element, text).width;
}

/**
 * Berechnet die TextMetrics zu einem Element als Kontext (für die Font-Ermittlung) und einem Text.
 * @param {HTMLElement} domElement
 * @param {string} text
 * @returns {TextMetrics}
 */
export function measureTextMetrics(domElement: HTMLElement, text: string): TextMetrics {
  const el = domElement as HTMLElementWithExtensions;
  let context = el.___measureTextContext;
  if (context == null) {
    const canvas = document.createElement("canvas");
    context = el.___measureTextContext = canvas.getContext("2d")!;
    context.font = getComputedStyle(domElement).font;
  }
  return context.measureText(text);
}

/**
 * zentriert ein Element absolut in der Mitte des Windows
 * @param {HTMLElement} element
 */
export function center(element: HTMLElement): void {
  element.style.position = "absolute";
  element.style.top = Math.max(
    0,
    (window.innerHeight - element.offsetHeight) / 2 + window.scrollY
  ) + "px";
  element.style.left = Math.max(
    0,
    (window.innerWidth - element.offsetWidth) / 2 + window.scrollX
  ) + "px";
}

/**
 * entfernt ein Element aus dem Dom
 * @param {HTMLElement} element
 * */
export function removeElement(element: HTMLElement | null | undefined): void {
  if (element?.parentNode) {
    element.parentNode.removeChild(element);
  }
}

/**
 * setzt eine Class, falls bool true, falls false entfernt sie
 * @param {HTMLElement} element
 * @param {string} className
 * @param {boolean} bool
 */
export function setClassIf(
  element: HTMLElement | null,
  className: string,
  bool: boolean
): void {
  if (element == null) {
    return;
  }
  if (bool) {
    element.classList.add(className);
  } else {
    element.classList.remove(className);
  }
}

/**
 * toggled eine class
 * @param {HTMLElement} element
 * @param {string} className
 */
export function toggleClass(element: HTMLElement, className: string): void {
  const has = element.classList.contains(className);
  setClassIf(element, className, !has);
}

/**
 * setzt ein Attribut, falls bool true, falls false entfernt es
 * @param {HTMLElement} element
 * @param {string} attribute
 * @param {boolean} bool
 */
export function setAttrIf(
  element: HTMLElement | null,
  attribute: string,
  bool: boolean,
  value: string | undefined = undefined
): void {
  if (element == null) {
    return;
  }
  if (bool) {
    element.setAttribute(attribute, value ?? "");
  } else {
    element.removeAttribute(attribute);
  }
}

const displayBeforeHiddenMap = new WeakMap<HTMLElement, string>();

/**
 * Zeigt ein Element an, wenn true, sonst wird es versteckt.
 * Die Methode setzt das display property auf 'none', wenn es versteckt wird,
 * und auf den vorigen Wert, wenn es angezeigt wird bzw. displayShowValueDefault,
 * wenn dieser nicht ermittelt werden kann.
 * @param {HTMLElement} element
 * @param {boolean} bool
 * @param {string} displayShowValueDefault optional sonst 'block'
 */
export function showIf(
  element: HTMLElement | null | (() => void),
  bool: boolean,
  displayShowValueDefault: string = "block"
): void {
  if (element == null) {
    return;
  }

  if (element instanceof Function) {
    element();
    return;
  }

  if (bool) {
    element.style.display = displayBeforeHiddenMap.get(element) ?? displayShowValueDefault;
  } else {
    if (element.style.display != "none") {
      displayBeforeHiddenMap.set(element, element.style.display);
    }
    element.style.display = "none";
  }
}

/**
 * setzt ein display:none Element auf block
 * @param {HTMLElement} element
 * @param {string} displayShowValue
 */
export function show(element: HTMLElement, displayShowValue: string = "block"): void {
  showIf(element, true, displayShowValue);
}

/**
 * setzt Element auf display:none
 * @param {HTMLElement} element
 */
export function hide(element: HTMLElement): void {
  showIf(element, false);
}

/**
 * Setzt die Klasse 'disabled' oder bei Form-Elementen das Attribut disabled, wenn true,
 * sonst wird sie/es entfernt
 * @param {HTMLElement} element
 * @param {boolean} bool
 */
export function disable(element: HTMLElement, bool: boolean = true): void {
  const tagName = element.getAttribute("tagName");
  if (tagName && /^(button|input|textarea|select)$/gi.test(tagName)) {
    setAttrIf(element, "disabled", bool);
  } else {
    setClassIf(element, "disabled", bool);
  }
}

/**
 * ermittelt zu einer css-variablen den Wert
 * @param {string} cssVariableName
 * @param {HTMLElement} root
 * @returns {string}
 */
export function getCssVar(
  cssVariableName: string,
  root: HTMLElement = document.documentElement
): string {
  return getComputedStyle(root).getPropertyValue(cssVariableName).replace(/\s/g, "");
}

/**
 * ermittelt, ob ein Element in einem container sichtbar ist oder aus dem Bereich herausgescrolled ist
 * @param {HTMLElement} element
 * @param {HTMLElement} container
 * @param {boolean} x
 * @param {boolean} y
 * @returns {boolean}
 */
export function isScrolledIntoView(
  element: HTMLElement,
  container: HTMLElement,
  x: boolean = true,
  y: boolean = true
): boolean {
  const a = element.getBoundingClientRect();
  const b = container.getBoundingClientRect();
  return (
    (!y || (a.top >= b.top && a.bottom <= b.bottom)) &&
    (!x || (a.left >= b.left && a.right <= b.right))
  );
}

/**
 * ruft scrollIntoView nur dann auf, wenn das Element ausserhalb des sichtbaren Bereiches liegt
 * @param {HTMLElement} element
 * @param {HTMLElement} container
 * @param {boolean} x
 * @param {boolean} y
 * @param {Object} options
 */
export function scrollIntoViewIfNeeded(
  element: HTMLElement | null,
  container: HTMLElement,
  options: ScrollIntoViewOptions | undefined = undefined,
  x: boolean = true,
  y: boolean = true
): void {
  if (element == null || isScrolledIntoView(element, container, x, y)) {
    return;
  }
  element.scrollIntoView(options);
}

/**
 * ruft scrollIntoView nur dann auf, wenn das Element ausserhalb des sichtbaren Bereiches liegt
 * @param {HTMLElement} element
 */
export function scrollBottom(element: HTMLElement | null): void {
  if (element == null) {
    return;
  }
  element.scrollTop = element.scrollHeight - element.clientHeight;
}

/**
 * speichert zu einem Dom-Element und einem key ein beliebiges data-Ding. Emuliert Jquery .data,
 * speichert allerdings nicht als DOM-Attribut, sondern in einer WeakMap.
 * @param {HTMLElement} element
 * @param {string} key
 * @param {any} data
 */
export function setData<T = unknown>(element: HTMLElement, key: string, data: T): void {
  let dataMap = weakDataMap.get(element);
  if (dataMap == null) {
    dataMap = {};
    weakDataMap.set(element, dataMap);
  }
  dataMap[key] = data;
}

/**
 * lesen der Daten zu setData
 * @param {HTMLElement} element
 * @param {string} key
 * @returns {any}
 */
export function getData<T = unknown>(element: HTMLElement, key: string): T | undefined {
  const dataMap = weakDataMap.get(element);
  return dataMap?.[key] as T | undefined;
}

/**
 * gibt ein Objekt mit allen data - Attribute - key / values zurück, analog zu Jquery.data,
 * allerdings nur reine Data-Attribute mit strings als values!
 *
 * @param {HTMLElement} element
 */
export function data(element: HTMLElement): Record<string, string> {
  const result: Record<string, string> = {};
  for (const a of element.attributes) {
    if (a.name.startsWith("data-")) {
      result[a.name.substring(5)] = a.value;
    }
  }
  return result;
}

/**
 * setzt den css Style ins Element, analog zu Jquery css
 * @param {HTMLElement} element
 * @param {Object} styleObject
 */
export function css(element: HTMLElement, styleObject: Partial<CSSStyleDeclaration>): void {
  Object.assign(element.style, styleObject);
}

/**
 * löscht alle Children eines elementes
 * @param {HTMLElement} element
 */
export function empty(element: HTMLElement | null): HTMLElement | null {
  while (element?.firstElementChild) {
    element.firstElementChild.remove();
  }
  return element;
}

/**
 * like JQuery fadeOut
 * @param {HTMLElement} element
 * @param {number} duration in ms
 * @returns {Promise} called when duration ends
 */
export function fadeOut(element: HTMLElement, duration: number = 300): Promise<void> {
  return new Promise(async (resolve) => {
    element.style.transition = `opacity ${duration}ms`;
    element.style.opacity = "0";
    await Commons.sleep(duration);
    resolve();
  });
}

/**
 * like JQuery fadeIn
 * @param {HTMLElement} element
 * @param {number} duration in ms
 * @returns {Promise} called when duration ends
 */
export function fadeIn(element: HTMLElement, duration: number = 300): Promise<void> {
  return new Promise(async (resolve) => {
    element.style.transition = `opacity ${duration}ms`;
    element.style.opacity = "1";
    await Commons.sleep(duration);
    resolve();
  });
}

/**
 * is an element not visible: 'hidden' and not display: 'none' and not opacity: 0?
 * @param {HTMLElement} element
 * @returns {boolean}
 */
export function isVisible(element: HTMLElement): boolean {
  const style = window.getComputedStyle(element);
  return (
    parseFloat(style.getPropertyValue("opacity")) > 0 &&
    style.getPropertyValue("visibility") != "hidden" &&
    style.getPropertyValue("display") != "none"
  );
}

/**
 * JSX: sorgt dafür, dass ein Callback genau nur ein Mal beim Initialisieren des Dom-Elementes mit dem Dom-Element aufgerufen wird.
 * Beispiel: <div ref={Utils.onRefInit(ref=>console.log(ref))} />
 * @param callback
 * @return {function(...[*]=)}
 */
export const onRefInit =
  <T extends HTMLElement>(callback: (element: T) => void) =>
    (reactReference: T | null): void => {
      if (reactReference == null) {
        return;
      }
      const el = reactReference as HTMLElementWithExtensions;
      if (el.__onRefInitialized) {
        return;
      }
      el.__onRefInitialized = true;
      callback(reactReference);
    };

/**
 * Konvertiert einen style-String als style-css-Objekt für React
 * @param stringStyles
 * @returns {*}
 */
export const convertStylesStringToObject = (
  stringStyles: string | Record<string, string>
): Record<string, string> => {
  if (typeof stringStyles !== "string") {
    return {};
  }

  return stringStyles.split(";").reduce((acc, style) => {
    const colonPosition = style.indexOf(":");

    if (colonPosition === -1) {
      return acc;
    }

    const camelCaseProperty = style
      .substr(0, colonPosition)
      .trim()
      .replace(/^-ms-/, "ms-")
      .replace(/-./g, (c) => c.substr(1).toUpperCase()),
      value = style.substr(colonPosition + 1).trim();

    return value ? { ...acc, [camelCaseProperty]: value } : acc;
  }, {} as Record<string, string>);
};

/**
 * Fügt ein neues Element am Anfang des Elternelements ein.
 * Wenn das Elternelement keine Kinder hat, wird das neue Element direkt angehängt.
 * @param {HTMLElement} parentEl
 * @param {HTMLElement} newElement
 */
export function prependElement(
  parentEl: HTMLElement | null,
  newElement: HTMLElement | null
): void {
  if (parentEl == null || newElement == null) {
    return;
  }
  if (parentEl.children.length > 0) {
    parentEl.insertBefore(newElement, parentEl.children[0]);
  } else {
    parentEl.appendChild(newElement);
  }
}
